import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { listAppointments, updateAppointment } from '../../api/appointments'
import type { AppointmentListItem } from '../../types/appointments'
import { TYPE_COLORS } from '../../types/appointments'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import AppointmentBookingModal from './AppointmentBookingModal'

// ── Calendar constants ────────────────────────────────────────────────────────

const SLOT_HEIGHT = 26      // px per 15-min slot (104 px/hour, same visual scale as before)
const CAL_START_HOUR = 7    // 7:00 AM
const CAL_END_HOUR = 19     // 7:00 PM (exclusive)
const NUM_SLOTS = (CAL_END_HOUR - CAL_START_HOUR) * 4  // 48 slots

// ── Helpers ───────────────────────────────────────────────────────────────────

function getMondayOfWeek(d: Date): Date {
  const day = d.getDay()  // 0=Sun
  const diff = day === 0 ? -6 : 1 - day
  const monday = new Date(d)
  monday.setDate(d.getDate() + diff)
  monday.setHours(0, 0, 0, 0)
  return monday
}

function atMidnight(d: Date): Date {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}

function weekdayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short' })
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(d.getDate() + n)
  return r
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
}

function toLocalDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function slotToTime(slotIdx: number): string {
  const mins = CAL_START_HOUR * 60 + slotIdx * 15
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}

function formatWeekRange(start: Date, end: Date): string {
  const s = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  const e = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  return `${s} – ${e}`
}

function formatDayHeading(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function getApptTop(scheduledAt: string): number {
  const d = new Date(scheduledAt)
  const minsFromCalStart = (d.getHours() - CAL_START_HOUR) * 60 + d.getMinutes()
  return (minsFromCalStart / 15) * SLOT_HEIGHT
}

function getApptHeight(durationMinutes: number): number {
  return Math.max((durationMinutes / 15) * SLOT_HEIGHT - 3, 18)
}

interface PositionedAppointment {
  appt: AppointmentListItem
  top: number
  height: number
  col: number
  totalCols: number
}

// Lays out same-day appointments side by side when their time ranges overlap,
// so concurrent bookings don't stack invisibly on top of one another.
function layoutDayAppointments(appts: AppointmentListItem[]): PositionedAppointment[] {
  const sorted = [...appts].sort(
    (a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
  )

  interface Interval { appt: AppointmentListItem; start: number; end: number; col: number }
  const intervals: Interval[] = sorted.map(appt => {
    const start = new Date(appt.scheduled_at).getTime()
    return { appt, start, end: start + appt.duration_minutes * 60_000, col: -1 }
  })

  // Group into clusters of transitively-overlapping intervals
  const clusters: Interval[][] = []
  let current: Interval[] = []
  let clusterEnd = -Infinity
  for (const iv of intervals) {
    if (current.length === 0 || iv.start < clusterEnd) {
      current.push(iv)
      clusterEnd = Math.max(clusterEnd, iv.end)
    } else {
      clusters.push(current)
      current = [iv]
      clusterEnd = iv.end
    }
  }
  if (current.length) clusters.push(current)

  const result: PositionedAppointment[] = []
  for (const cluster of clusters) {
    // Greedily assign each interval the first column whose previous occupant has already ended
    const colEndTimes: number[] = []
    for (const iv of cluster) {
      let assigned = colEndTimes.findIndex(end => iv.start >= end)
      if (assigned === -1) { assigned = colEndTimes.length; colEndTimes.push(iv.end) }
      else { colEndTimes[assigned] = iv.end }
      iv.col = assigned
    }
    const totalCols = colEndTimes.length
    for (const iv of cluster) {
      result.push({
        appt: iv.appt,
        top: getApptTop(iv.appt.scheduled_at),
        height: getApptHeight(iv.appt.duration_minutes),
        col: iv.col,
        totalCols,
      })
    }
  }
  return result
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface BookingSlot {
  date: string
  time: string
  duration: number
}

interface DragState {
  dayIdx: number
  startSlot: number
  currentSlot: number
}

interface MoveDragState {
  apptId: number
  originalDayIdx: number
  originalSlot: number
  durationSlots: number
  dayIdx: number
  slot: number
  moved: boolean
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AppointmentCalendar() {
  const [anchorDate, setAnchorDate] = useState(() => new Date())
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week')
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [dragMousePos, setDragMousePos] = useState<{ x: number; y: number } | null>(null)
  const [moveDrag, setMoveDrag] = useState<MoveDragState | null>(null)
  const [bookingSlot, setBookingSlot] = useState<BookingSlot | null>(null)

  const confirm = useConfirm()
  const toast = useToast()

  const scrollRef = useRef<HTMLDivElement>(null)
  const didInitialScroll = useRef(false)

  const weekStart = useMemo(() => getMondayOfWeek(anchorDate), [anchorDate])
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )
  const displayDays = useMemo(
    () => (viewMode === 'day' ? [atMidnight(anchorDate)] : weekDays),
    [viewMode, anchorDate, weekDays],
  )

  // Current time indicator
  const [nowMinutes, setNowMinutes] = useState(() => {
    const n = new Date()
    return n.getHours() * 60 + n.getMinutes()
  })
  useEffect(() => {
    const timer = setInterval(() => {
      const n = new Date()
      setNowMinutes(n.getHours() * 60 + n.getMinutes())
    }, 60_000)
    return () => clearInterval(timer)
  }, [])

  // Scroll to current time on first mount
  useEffect(() => {
    if (didInitialScroll.current || !scrollRef.current) return
    didInitialScroll.current = true
    const minutesFromCalStart = nowMinutes - CAL_START_HOUR * 60 - 60  // 1 hour before now
    scrollRef.current.scrollTop = Math.max(0, (minutesFromCalStart / 15) * SLOT_HEIGHT)
  })

  // Fetch appointments for the current week
  useEffect(() => {
    setLoading(true)
    const weekEnd = addDays(weekStart, 6)
    listAppointments({
      date_from: `${toLocalDateString(weekStart)}T00:00:00Z`,
      date_to: `${toLocalDateString(weekEnd)}T23:59:59Z`,
      limit: 200,
    })
      .then(setAppointments)
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false))
  }, [weekStart, refreshKey])

  const goPrev = () => setAnchorDate(d => addDays(d, viewMode === 'day' ? -1 : -7))
  const goNext = () => setAnchorDate(d => addDays(d, viewMode === 'day' ? 1 : 7))
  const goToday = () => setAnchorDate(new Date())

  // Group appointments by day index
  const appointmentsByDay = useMemo(() => {
    const map = new Map<number, AppointmentListItem[]>()
    for (let i = 0; i < displayDays.length; i++) map.set(i, [])
    for (const appt of appointments) {
      const d = new Date(appt.scheduled_at)
      const idx = displayDays.findIndex(wd => isSameDay(wd, d))
      if (idx !== -1) map.get(idx)!.push(appt)
    }
    return map
  }, [appointments, displayDays])

  // Drag handlers
  const handleSlotMouseDown = (dayIdx: number, slotIdx: number, e: React.MouseEvent) => {
    e.preventDefault()
    setDrag({ dayIdx, startSlot: slotIdx, currentSlot: slotIdx })
    setDragMousePos({ x: e.clientX, y: e.clientY })
  }

  const handleSlotMouseEnter = (dayIdx: number, slotIdx: number) => {
    setDrag(d => (d && d.dayIdx === dayIdx) ? { ...d, currentSlot: slotIdx } : d)
    setMoveDrag(m => {
      if (!m) return m
      const moved = m.moved || dayIdx !== m.originalDayIdx || slotIdx !== m.originalSlot
      return { ...m, dayIdx, slot: slotIdx, moved }
    })
  }

  // Commit drag/click on mouseup, track cursor for the duration tooltip (registered globally while dragging)
  useEffect(() => {
    if (!drag) return
    const handleUp = () => {
      const { dayIdx, startSlot, currentSlot } = drag
      const minSlot = Math.min(startSlot, currentSlot)
      const maxSlot = Math.max(startSlot, currentSlot)
      setBookingSlot({
        date: toLocalDateString(displayDays[dayIdx]),
        time: slotToTime(minSlot),
        duration: (maxSlot - minSlot + 1) * 15,
      })
      setDrag(null)
      setDragMousePos(null)
    }
    const handleMove = (e: MouseEvent) => setDragMousePos({ x: e.clientX, y: e.clientY })
    window.addEventListener('mouseup', handleUp)
    window.addEventListener('mousemove', handleMove)
    return () => {
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('mousemove', handleMove)
    }
  }, [drag, displayDays])

  const dragDurationMinutes = drag ? (Math.abs(drag.currentSlot - drag.startSlot) + 1) * 15 : 0

  // Start dragging an *existing* appointment to reschedule it
  const handleApptMouseDown = (appt: AppointmentListItem, dayIdx: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (appt.status === 'completed' || appt.status === 'cancelled') return
    e.preventDefault()
    const originalSlot = Math.round(getApptTop(appt.scheduled_at) / SLOT_HEIGHT)
    const durationSlots = Math.max(1, Math.round(appt.duration_minutes / 15))
    setMoveDrag({ apptId: appt.id, originalDayIdx: dayIdx, originalSlot, durationSlots, dayIdx, slot: originalSlot, moved: false })
  }

  // Commit an appointment move on mouseup: confirm, then PATCH the new time
  useEffect(() => {
    if (!moveDrag) return
    const handleUp = () => {
      const snapshot = moveDrag
      setMoveDrag(null)
      if (!snapshot.moved) return
      const appt = appointments.find(a => a.id === snapshot.apptId)
      const newDay = displayDays[snapshot.dayIdx]
      if (!appt || !newDay) return
      const newDate = toLocalDateString(newDay)
      const newTime = slotToTime(snapshot.slot)
      const dateLabel = newDay.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      const who = appt.patient_name ? `${appt.patient_name}'s` : 'this'
      void (async () => {
        const ok = await confirm({
          title: 'Reschedule appointment?',
          message: `Move ${who} appointment to ${dateLabel} at ${newTime}?`,
          confirmLabel: 'Reschedule',
          danger: false,
        })
        if (!ok) return
        try {
          await updateAppointment(appt.id, { scheduled_at: new Date(`${newDate}T${newTime}:00`).toISOString() })
          toast.success('Appointment rescheduled.')
          setRefreshKey(k => k + 1)
        } catch {
          toast.error('Failed to reschedule appointment.')
        }
      })()
    }
    window.addEventListener('mouseup', handleUp)
    return () => window.removeEventListener('mouseup', handleUp)
  }, [moveDrag, appointments, displayDays, confirm, toast])

  const isSlotInDrag = (dayIdx: number, slotIdx: number): boolean => {
    if (!drag || drag.dayIdx !== dayIdx) return false
    const min = Math.min(drag.startSlot, drag.currentSlot)
    const max = Math.max(drag.startSlot, drag.currentSlot)
    return slotIdx >= min && slotIdx <= max
  }

  const isSlotMoveTarget = (dayIdx: number, slotIdx: number): boolean => {
    if (!moveDrag || moveDrag.dayIdx !== dayIdx) return false
    return slotIdx >= moveDrag.slot && slotIdx < moveDrag.slot + moveDrag.durationSlots
  }

  const today = new Date()
  const nowTop = (nowMinutes - CAL_START_HOUR * 60) / 15 * SLOT_HEIGHT
  const nowVisible = nowTop >= 0 && nowTop <= NUM_SLOTS * SLOT_HEIGHT
  const todayDayIdx = displayDays.findIndex(d => isSameDay(d, today))

  return (
    <>
      {/* ── Navigation ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
        <button onClick={goPrev} style={navBtnStyle}>
          <ChevronLeft size={16} />
        </button>
        <button onClick={goNext} style={navBtnStyle}>
          <ChevronRight size={16} />
        </button>
        <span style={{ fontWeight: 600, color: 'var(--text-heading)', fontSize: '0.92rem', marginRight: '0.25rem' }}>
          {viewMode === 'day' ? formatDayHeading(anchorDate) : formatWeekRange(weekStart, addDays(weekStart, 6))}
        </span>
        <button onClick={goToday} style={todayBtnStyle}>Today</button>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
          <button
            onClick={() => setViewMode('week')}
            style={{ ...viewModeBtnStyle, ...(viewMode === 'week' ? viewModeBtnActiveStyle : {}) }}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('day')}
            style={{ ...viewModeBtnStyle, ...(viewMode === 'day' ? viewModeBtnActiveStyle : {}), borderLeft: '1px solid var(--border)' }}
          >
            Day
          </button>
        </div>
        {loading && <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginLeft: '0.25rem' }}>Loading…</span>}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-4)', marginLeft: 'auto' }}>
          Click or drag empty slots to schedule · drag an appointment to reschedule it
        </span>
      </div>

      {/* ── Calendar grid ────────────────────────────────────────────────────── */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', background: 'var(--surface)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

          {/* Scrollable body — header lives inside so it shares the same width as the columns */}
        <div ref={scrollRef} style={{ overflowY: 'auto', maxHeight: '620px' }}>

          {/* Day header row — sticky inside the scroll container */}
          <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', borderBottom: '2px solid var(--border-input)', background: 'var(--surface-alt)' }}>
            <div style={{ width: '56px', flexShrink: 0 }} />
            {displayDays.map((day, i) => {
              const isToday = isSameDay(day, today)
              return (
                <div key={i} style={{ flex: 1, textAlign: 'center', padding: '0.55rem 0.2rem', borderLeft: '1px solid var(--border-input)' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {weekdayLabel(day)}
                  </div>
                  <div style={{
                    fontSize: '1rem', fontWeight: 700, marginTop: '2px',
                    color: isToday ? 'var(--accent-contrast)' : 'var(--text-heading)',
                    background: isToday ? 'var(--accent)' : 'transparent',
                    borderRadius: '50%', width: '28px', height: '28px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {day.getDate()}
                  </div>
                </div>
              )
            })}
          </div>

          <div style={{ display: 'flex' }}>

            {/* Time gutter */}
            <div style={{ width: '56px', flexShrink: 0 }}>
              {Array.from({ length: NUM_SLOTS }, (_, i) => {
                const isHour = i % 4 === 0
                const isHalf = i % 2 === 0 && !isHour
                return (
                  <div key={i} style={{
                    height: `${SLOT_HEIGHT}px`,
                    borderTop: `1px solid ${isHour ? 'var(--border-input)' : isHalf ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.06)'}`,
                    paddingRight: '8px', display: 'flex', alignItems: 'flex-start',
                    justifyContent: 'flex-end', paddingTop: '3px',
                    fontSize: '0.66rem', color: 'var(--text-4)', fontWeight: 500,
                    userSelect: 'none',
                  }}>
                    {isHour ? slotToTime(i) : ''}
                  </div>
                )
              })}
            </div>

            {/* Day columns */}
            {displayDays.map((_, dayIdx) => {
              const dayAppts = appointmentsByDay.get(dayIdx) ?? []
              const isToday = dayIdx === todayDayIdx

              return (
                <div key={dayIdx} style={{ flex: 1, position: 'relative', borderLeft: '1px solid var(--border-input)', background: isToday ? 'rgba(26,107,90,0.025)' : undefined }}>

                  {/* Slot cells (click + drag targets) */}
                  {Array.from({ length: NUM_SLOTS }, (_, slotIdx) => {
                    const inDrag = isSlotInDrag(dayIdx, slotIdx)
                    const inMoveTarget = isSlotMoveTarget(dayIdx, slotIdx)
                    const isHour = slotIdx % 4 === 0
                    const isHalf = slotIdx % 2 === 0 && !isHour
                    return (
                      <div
                        key={slotIdx}
                        style={{
                          height: `${SLOT_HEIGHT}px`,
                          borderTop: `1px solid ${isHour ? 'var(--border-input)' : isHalf ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.06)'}`,
                          background: inMoveTarget ? 'rgba(26,107,90,0.22)' : inDrag ? 'rgba(26,107,90,0.14)' : 'transparent',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background 0.05s',
                        }}
                        onMouseDown={e => handleSlotMouseDown(dayIdx, slotIdx, e)}
                        onMouseEnter={() => handleSlotMouseEnter(dayIdx, slotIdx)}
                      />
                    )
                  })}

                  {/* Current time indicator */}
                  {isToday && nowVisible && (
                    <div style={{
                      position: 'absolute', left: 0, right: 0, top: `${nowTop}px`,
                      height: '2px', background: 'var(--danger-text)', zIndex: 5, pointerEvents: 'none',
                    }}>
                      <div style={{
                        position: 'absolute', left: '-4px', top: '-4px',
                        width: '10px', height: '10px', borderRadius: '50%', background: 'var(--danger-text)',
                      }} />
                    </div>
                  )}

                  {/* Appointment blocks — laid out side by side when times overlap */}
                  {layoutDayAppointments(dayAppts).map(({ appt, top, height, col, totalCols }) => {
                    const { color, bg } = TYPE_COLORS[appt.appointment_type]
                    const colWidthPct = 100 / totalCols
                    const isFinalised = appt.status === 'completed' || appt.status === 'cancelled'
                    const isBeingMoved = moveDrag?.apptId === appt.id
                    return (
                      <Link
                        key={appt.id}
                        to={`/appointments/${appt.id}`}
                        onMouseDown={e => handleApptMouseDown(appt, dayIdx, e)}
                        style={{
                          position: 'absolute', top: `${top}px`,
                          left: `calc(${col * colWidthPct}% + 2px)`,
                          width: `calc(${colWidthPct}% - ${totalCols > 1 ? 3 : 4}px)`,
                          height: `${height}px`, zIndex: 3,
                          background: bg, borderLeft: `3px solid ${color}`,
                          borderRadius: '5px', padding: '3px 6px', overflow: 'hidden',
                          textDecoration: 'none', cursor: isFinalised ? 'pointer' : 'grab', display: 'block',
                          boxSizing: 'border-box',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                          opacity: isBeingMoved ? 0.35 : 1,
                          // Let mouse events fall through to the slot cells underneath while any
                          // appointment is being dragged, so the drop target keeps tracking the cursor.
                          pointerEvents: moveDrag ? 'none' : 'auto',
                        }}
                      >
                        <div style={{ fontSize: '0.73rem', fontWeight: 700, color, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {appt.patient_name ?? '—'}
                        </div>
                        {height > 30 && (
                          <div style={{ fontSize: '0.68rem', color, opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appt.client_name}
                          </div>
                        )}
                        {height > 48 && appt.reason && (
                          <div style={{ fontSize: '0.66rem', color, opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appt.reason}
                          </div>
                        )}
                      </Link>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Drag duration tooltip */}
      {drag && dragMousePos && (
        <div style={{
          position: 'fixed', left: `${dragMousePos.x + 14}px`, top: `${dragMousePos.y - 12}px`,
          zIndex: 50, pointerEvents: 'none',
          background: 'var(--sidebar-bg)', color: '#fff',
          padding: '0.3rem 0.6rem', borderRadius: '6px',
          fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        }}>
          {formatDuration(dragDurationMinutes)}
        </div>
      )}

      {/* Booking modal */}
      {bookingSlot && (
        <AppointmentBookingModal
          initialDate={bookingSlot.date}
          initialTime={bookingSlot.time}
          initialDuration={bookingSlot.duration}
          onClose={() => setBookingSlot(null)}
          onCreated={() => {
            setBookingSlot(null)
            setRefreshKey(k => k + 1)
          }}
        />
      )}
    </>
  )
}

// ── Button styles ─────────────────────────────────────────────────────────────

const navBtnStyle: React.CSSProperties = {
  padding: '0.35rem', background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: '6px', cursor: 'pointer', color: 'var(--text-2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}

const todayBtnStyle: React.CSSProperties = {
  padding: '0.3rem 0.8rem', background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
  color: 'var(--accent)',
}

const viewModeBtnStyle: React.CSSProperties = {
  padding: '0.3rem 0.7rem', background: 'var(--surface)', border: 'none',
  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-3)',
}

const viewModeBtnActiveStyle: React.CSSProperties = {
  background: 'var(--accent)', color: 'var(--accent-contrast)',
}
